import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadCustomerPayment} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadCustomerPayment,
  createZadCustomerPayment,
  fetchZadCustomerAccountSummary,
  fetchZadCustomerPaymentById,
  fetchZadCustomerPaymentPickerCustomers,
  fetchZadCustomerPaymentPickerSales,
  fetchZadCustomerPayments,
  fetchZadSalePaymentSummary,
} from './service'
import {FetchZadCustomerPaymentsResponse, ZadCustomerPaymentState} from './types'

const initialState: ZadCustomerPaymentState = {
  payments: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedPayment: null,
  customerSummary: null,
  saleSummaries: {},
  pickerCustomers: [],
  pickerSales: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadCustomerPaymentState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadCustomerPaymentState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertPayment(state: ZadCustomerPaymentState, payment?: ZadCustomerPayment | null) {
  if (!payment?._id) return
  state.selectedPayment = payment
  const index = state.payments.findIndex((item) => item._id === payment._id)
  if (index >= 0) {
    state.payments[index] = payment
  }
}

const zadCustomerPaymentSlice = createSlice({
  name: 'zadCustomerPayments',
  initialState,
  reducers: {
    clearZadCustomerPaymentError: (state) => {
      clearError(state)
    },
    clearSelectedZadCustomerPayment: (state) => {
      state.selectedPayment = null
    },
    clearZadCustomerAccountSummary: (state) => {
      state.customerSummary = null
    },
    clearZadCustomerPaymentPickerSales: (state) => {
      state.pickerSales = []
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadCustomerPayments.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadCustomerPayments.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadCustomerPayments.fulfilled,
      (state, action: PayloadAction<FetchZadCustomerPaymentsResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.payments = action.payload.payments
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadCustomerPaymentById.pending, (state) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadCustomerPaymentById.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadCustomerPaymentById.fulfilled, (state, action) => {
      state.actionLoading = false
      state.selectedPayment = action.payload.payment
    })

    builder.addCase(createZadCustomerPayment.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadCustomerPayment.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadCustomerPayment.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPayment(state, action.payload.payment)
    })

    builder.addCase(cancelZadCustomerPayment.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(cancelZadCustomerPayment.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(cancelZadCustomerPayment.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPayment(state, action.payload.payment)
    })

    builder.addCase(fetchZadCustomerAccountSummary.fulfilled, (state, action) => {
      state.customerSummary = action.payload.summary
    })
    builder.addCase(fetchZadCustomerAccountSummary.rejected, (state) => {
      state.customerSummary = null
    })

    builder.addCase(fetchZadSalePaymentSummary.fulfilled, (state, action) => {
      state.saleSummaries[action.payload.summary.saleId] = action.payload.summary
    })

    builder.addCase(fetchZadCustomerPaymentPickerCustomers.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadCustomerPaymentPickerCustomers.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadCustomerPaymentPickerCustomers.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerCustomers = action.payload.customers
    })

    builder.addCase(fetchZadCustomerPaymentPickerSales.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadCustomerPaymentPickerSales.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadCustomerPaymentPickerSales.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerSales = action.payload.sales
    })
  },
})

export default zadCustomerPaymentSlice.reducer
export const actions = zadCustomerPaymentSlice.actions
export {initialState as zadCustomerPaymentInitialState}
